import { BREVET_FORMULAS } from "../constants/brevetFormulas"
import {
    BREVET_SUBJECTS,
    getModulesForSubjects,
} from "../constants/brevetModules"

/**
 * Logique V1 — règles simples étendues round 3 avec floor/ceiling + mismatch.
 *
 * Inputs : { subjects, objective, brevetBlanc, niveauActuel, autonomie, timePerWeekend }
 *
 * Output :
 * {
 *   formula,         BrevetFormula : formule recommandée finale = min(floor, ceiling)
 *   floor,           BrevetFormula : formule idéale pédagogiquement
 *   ceiling,         BrevetFormula : formule max compatible avec timePerWeekend
 *   mismatch,        boolean       : floor > ceiling — l'utilisateur n'a pas assez de temps
 *                                     pour la formule idéale ; surfacer un upsell.
 *   reason,          string[]      : raisons textuelles (existant)
 *   priorityModules, Module[]      : modules ciblés (existant, alignés sur formula)
 * }
 *
 * Floor (idéal pédagogique) :
 * - Intensif si objectif === "mention_tb"
 *   OU subjects.length >= 3
 *   OU (brevetBlanc === "en_difficulte" && timePerWeekend === "4h_plus")
 *   OU (niveauActuel === "en_difficulte" && autonomie === "jamais")
 * - Découverte si objectif === "diplome" && subjects.length === 1
 *   && timePerWeekend === "1h" && niveauActuel !== "en_difficulte"
 * - Sprint sinon
 *
 * Ceiling (cap par temps dispo) :
 * - "1h"      → Découverte (~2h/WE max)
 * - "2_3h"    → Sprint
 * - "4h_plus" → Intensif (pas de cap)
 * - null/undefined → Intensif (pas de signal)
 *
 * Recommandation = min(floor, ceiling).
 * Mismatch = floor > ceiling.
 */

const SECONDARY_MODULE_IDS = [
    "maths_scratch_stats",
    "fr_grammaire",
    "hg_doc",
    "sc_calculs",
    "oral_contenu",
]

const FORMULA_ORDER = ["decouverte", "sprint", "intensif"]
const cmpFormulas = (a, b) =>
    FORMULA_ORDER.indexOf(a) - FORMULA_ORDER.indexOf(b)

const subjectLabels = (ids) =>
    ids
        .map((id) => BREVET_SUBJECTS.find((s) => s.id === id)?.short)
        .filter(Boolean)

const computeFloor = ({
    subjects,
    objective,
    brevetBlanc,
    niveauActuel,
    autonomie,
    timePerWeekend,
}) => {
    if (
        objective === "mention_tb" ||
        subjects.length >= 3 ||
        (brevetBlanc === "en_difficulte" && timePerWeekend === "4h_plus") ||
        (niveauActuel === "en_difficulte" && autonomie === "jamais")
    ) {
        return "intensif"
    }
    if (
        objective === "diplome" &&
        subjects.length === 1 &&
        timePerWeekend === "1h" &&
        niveauActuel !== "en_difficulte"
    ) {
        return "decouverte"
    }
    return "sprint"
}

const computeCeiling = ({ timePerWeekend }) => {
    switch (timePerWeekend) {
        case "1h":
            return "decouverte"
        case "2_3h":
            return "sprint"
        case "4h_plus":
            return "intensif"
        default:
            // null/undefined → pas de signal, pas de cap
            return "intensif"
    }
}

const buildReason = ({
    formulaId,
    subjects,
    objective,
    brevetBlanc,
    niveauActuel,
    autonomie,
    timePerWeekend,
}) => {
    const lines = []
    const subjectsLabel = subjectLabels(subjects).join(", ")

    if (niveauActuel === "en_difficulte") {
        lines.push(
            `Niveau actuel fragile sur ${subjectsLabel} — on remet les bases en priorité.`
        )
    } else if (niveauActuel === "bon_eleve") {
        lines.push(
            `Bon niveau actuel — on consolide pour viser plus haut.`
        )
    } else if (brevetBlanc === "moyens") {
        lines.push(
            `Brevet blanc moyen + matières fragiles (${subjectsLabel}) = cadre régulier nécessaire.`
        )
    } else if (brevetBlanc === "en_difficulte") {
        lines.push(
            `Brevet blanc difficile sur ${subjectsLabel} — fondamentaux à sécuriser.`
        )
    } else if (brevetBlanc === "ca_va") {
        lines.push(
            `Brevet blanc OK — on peut consolider ${subjectsLabel} pour viser plus haut.`
        )
    } else {
        lines.push(
            `Préparation amont sur ${subjectsLabel}.`
        )
    }

    if (autonomie === "jamais") {
        lines.push("Autonomie devoirs limitée — encadrement régulier indispensable.")
    } else if (autonomie === "toujours") {
        lines.push("Bonne autonomie devoirs — séances complétées par travail IA en semaine.")
    }

    if (timePerWeekend === "1h") {
        lines.push("Temps limité (~1h/week-end) — format adapté.")
    } else if (timePerWeekend === "2_3h") {
        lines.push("2-3h dispo / week-end — format Sprint adapté.")
    } else if (timePerWeekend === "4h_plus") {
        lines.push("4h+ dispo / week-end — couverture intensive possible.")
    }

    if (objective === "mention") {
        lines.push("Cible mention — matières fragiles en priorité.")
    } else if (objective === "mention_tb") {
        lines.push("Mention TB — tous les modules à fond.")
    } else {
        lines.push("Objectif diplôme — fondamentaux d'abord.")
    }

    return lines
}

const buildPriorityModules = ({ formulaId, subjects, objective }) => {
    const allModules = getModulesForSubjects(subjects)
    const formula = BREVET_FORMULAS[formulaId]

    if (!formula.maxModules) return allModules

    const principalModules = allModules.filter(
        (m) => !SECONDARY_MODULE_IDS.includes(m.id)
    )

    const sorted =
        objective === "diplome" ? principalModules : allModules

    return sorted.slice(0, formula.maxModules)
}

export const recommendFormula = ({
    subjects = [],
    objective,
    brevetBlanc,
    niveauActuel,
    autonomie,
    timePerWeekend,
}) => {
    if (!subjects.length || !objective) {
        return {
            formula: BREVET_FORMULAS.sprint,
            floor: BREVET_FORMULAS.sprint,
            ceiling: BREVET_FORMULAS.sprint,
            mismatch: false,
            reason: ["Sélection partielle — recommandation par défaut."],
            priorityModules: [],
        }
    }

    const floorId = computeFloor({
        subjects,
        objective,
        brevetBlanc,
        niveauActuel,
        autonomie,
        timePerWeekend,
    })
    const ceilingId = computeCeiling({ timePerWeekend })

    const mismatch = cmpFormulas(floorId, ceilingId) > 0
    const recommendedId = mismatch ? ceilingId : floorId

    return {
        formula: BREVET_FORMULAS[recommendedId],
        floor: BREVET_FORMULAS[floorId],
        ceiling: BREVET_FORMULAS[ceilingId],
        mismatch,
        reason: buildReason({
            formulaId: recommendedId,
            subjects,
            objective,
            brevetBlanc,
            niveauActuel,
            autonomie,
            timePerWeekend,
        }),
        priorityModules: buildPriorityModules({
            formulaId: recommendedId,
            subjects,
            objective,
        }),
    }
}
